<template>
  <div class="w-full max-w-full min-w-0 overflow-x-hidden" dir="rtl">
    <div v-if="isAuthorized">
      <!-- Header + filters -->
      <div class="bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-700 overflow-hidden mb-4 w-full max-w-full min-w-0">
        <header class="px-3 sm:px-4 py-4 bg-gray-900/50 border-b border-gray-700 min-w-0">
          <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 min-w-0">
            <div class="min-w-0 overflow-hidden">
              <h3 class="text-sm font-bold text-gray-100 truncate">مرجوعی‌ها</h3>
              <p class="text-[11px] text-gray-400 mt-1 truncate">
                فهرست کامل مرجوعی‌های حضوری این شریک — همین مبلغ از طلبش کم شده است
              </p>
            </div>

            <button
                @click="fetchData"
                :disabled="loading"
                class="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-gray-800 border border-gray-600 text-xs text-gray-200 hover:bg-gray-700 transition disabled:opacity-50 shrink-0"
            >
              <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
              بروزرسانی
            </button>
          </div>
        </header>

        <section class="px-3 sm:px-4 py-4 bg-gray-900/30 border-b border-gray-700 min-w-0 overflow-hidden">
          <div class="grid grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3 min-w-0">
            <div class="col-span-1 min-w-0">
              <label class="block text-[10px] sm:text-[11px] text-gray-400 mb-1.5 truncate">از تاریخ</label>
              <date placeholder="از تاریخ" v-model="fromDate" />
            </div>

            <div class="col-span-1 min-w-0">
              <label class="block text-[10px] sm:text-[11px] text-gray-400 mb-1.5 truncate">تا تاریخ</label>
              <date placeholder="تا تاریخ" v-model="toDate" />
            </div>

            <div class="col-span-2 lg:col-span-1 min-w-0">
              <label class="block text-[10px] sm:text-[11px] text-gray-400 mb-1.5 truncate">جستجو با بارکد / GTIN</label>
              <input
                  type="text"
                  v-model="gtin"
                  placeholder="مثلاً 6221234567890"
                  dir="ltr"
                  class="w-full min-w-0 bg-gray-800 border border-gray-600 rounded-xl px-2 sm:px-3 py-2 text-[11px] sm:text-xs text-gray-200 focus:outline-none focus:border-indigo-500"
              />
            </div>

            <div class="col-span-2 lg:col-span-2 flex items-end gap-2 min-w-0">
              <button @click="applyFilter" class="px-3 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-xs text-white transition shrink-0">
                اعمال فیلتر
              </button>
              <button v-if="fromDate || toDate || gtin" @click="clearFilter" class="px-3 py-2 rounded-xl bg-gray-800 border border-gray-600 text-xs text-gray-300 hover:bg-gray-700 transition shrink-0">
                پاک کردن (کل تاریخچه)
              </button>
            </div>
          </div>
        </section>

        <!-- Summary cards -->
        <section v-if="summary" class="px-3 sm:px-4 py-4 grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-3 min-w-0">
          <div class="rounded-xl bg-rose-600/10 border border-rose-500/20 px-4 py-3 min-w-0 overflow-hidden">
            <p class="text-[11px] text-rose-300/80 truncate">تعداد مرجوعی</p>
            <p class="text-lg font-bold text-rose-300 mt-1 truncate" dir="ltr">{{ formatNumber(summary.quantity) }}</p>
          </div>
          <div class="rounded-xl bg-rose-600/10 border border-rose-500/20 px-4 py-3 min-w-0 overflow-hidden">
            <p class="text-[11px] text-rose-300/80 truncate">مبلغ مرجوعی</p>
            <p class="text-lg font-bold text-rose-300 mt-1 truncate" dir="ltr">{{ formatMoney(summary.amount) }}</p>
          </div>
          <div class="rounded-xl bg-rose-600/10 border border-rose-500/20 px-4 py-3 min-w-0 overflow-hidden">
            <p class="text-[11px] text-rose-300/80 truncate">مبلغ کسرشده از طلب شریک</p>
            <p class="text-lg font-bold text-rose-300 mt-1 truncate" dir="ltr">{{ formatMoney(summary.amount) }}</p>
          </div>
        </section>

        <!-- Top 5 most-returned products -->
        <section v-if="topReturnedProducts.length" class="px-3 sm:px-4 pb-4 min-w-0">
          <div class="bg-gray-900/50 border border-gray-700 rounded-xl p-3 min-w-0">
            <h4 class="text-[11px] font-bold text-gray-300 mb-3">۵ محصول با بیشترین مرجوعی</h4>
            <div class="space-y-2">
              <div
                  v-for="(p, idx) in topReturnedProducts"
                  :key="p.product_id"
                  class="flex items-center justify-between gap-3 min-w-0 rounded-xl bg-gray-800/60 border border-gray-700/60 px-3 py-2"
              >
                <div class="flex items-center gap-2 min-w-0">
                  <span class="shrink-0 w-5 h-5 rounded-full bg-rose-600/20 text-rose-300 text-[10px] font-bold flex items-center justify-center">{{ idx + 1 }}</span>
                  <div
                      class="shrink-0 w-8 h-8 aspect-square rounded-lg overflow-hidden border border-gray-700 bg-gray-800 flex items-center justify-center"
                      :class="imgOk(p.image) ? 'cursor-zoom-in' : ''"
                      @click="zoomImage(p.image)"
                  >
                    <img v-if="imgOk(p.image)" :src="img_source(p.image)" class="w-full h-full object-cover" @error="markBroken(p.image)">
                    <i v-else class="fas fa-image text-gray-600 text-xs"></i>
                  </div>
                  <span class="text-xs text-gray-100 truncate">{{ p.product_name }}</span>
                </div>
                <div class="shrink-0 text-left" dir="ltr">
                  <p class="text-xs font-bold text-rose-300">{{ formatNumber(p.quantity) }} عدد</p>
                  <p class="text-[10px] text-gray-500">{{ formatMoney(p.amount) }}</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- Loading / error -->
      <div v-if="loading" class="p-10 text-center">
        <i class="fas fa-circle-notch fa-spin text-indigo-400 text-2xl"></i>
        <p class="text-gray-400 mt-3 text-sm">در حال دریافت اطلاعات...</p>
      </div>

      <div v-else-if="error" class="p-10 text-center">
        <i class="fas fa-exclamation-triangle text-red-400 text-2xl"></i>
        <p class="text-red-400 mt-3 text-sm">{{ error }}</p>
        <button @click="fetchData" class="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-xl text-white text-xs transition">
          تلاش مجدد
        </button>
      </div>

      <!-- Returns list -->
      <div v-else class="bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-700 overflow-hidden w-full max-w-full min-w-0">
        <header class="px-3 sm:px-4 py-3 bg-gray-900/50 border-b border-gray-700">
          <h4 class="text-xs font-bold text-gray-200">مرجوعی‌ها ({{ formatNumber(transactions.length) }})</h4>
        </header>

        <!-- Mobile cards -->
        <div class="md:hidden p-3 space-y-3 w-full max-w-full min-w-0 overflow-hidden">
          <article
              v-for="(tx, idx) in transactions"
              :key="idx"
              class="rounded-2xl bg-gray-900/45 border border-gray-700/80 p-3 shadow-sm w-full max-w-full min-w-0 overflow-hidden"
          >
            <div class="flex items-start justify-between gap-2 min-w-0">
              <div class="flex items-start gap-2 min-w-0">
                <div
                    class="shrink-0 w-10 h-10 aspect-square rounded-lg overflow-hidden border border-gray-700 bg-gray-800 flex items-center justify-center"
                    :class="imgOk(tx.image) ? 'cursor-zoom-in' : ''"
                    @click="zoomImage(tx.image)"
                >
                  <img v-if="imgOk(tx.image)" :src="img_source(tx.image)" class="w-full h-full object-cover" @error="markBroken(tx.image)">
                  <i v-else class="fas fa-image text-gray-600 text-sm"></i>
                </div>
                <p class="text-xs font-bold text-gray-100 leading-5 line-clamp-2 break-safe min-w-0">{{ tx.product_name }}</p>
              </div>
              <span class="text-[10px] text-gray-500 shrink-0">{{ tx.date | toJalali('jYYYY/jMM/jDD HH:mm') }}</span>
            </div>

            <div class="grid grid-cols-2 gap-2 mt-3 min-w-0 text-[11px]">
              <div class="min-w-0 overflow-hidden">
                <p class="text-gray-500 truncate">تعداد</p>
                <p class="text-gray-300 mt-0.5 truncate" dir="ltr">{{ formatNumber(tx.quantity) }}</p>
              </div>
              <div class="min-w-0 overflow-hidden">
                <p class="text-gray-500 truncate">مبلغ</p>
                <p class="text-rose-300 font-bold mt-0.5 truncate" dir="ltr">{{ formatMoney(tx.line_total) }}</p>
              </div>
            </div>

            <p class="text-[10px] text-gray-500 mt-3 pt-3 border-t border-gray-700/70 truncate" dir="ltr">{{ tx.order_reference }}</p>
          </article>

          <div v-if="transactions.length === 0" class="px-4 py-12 text-center rounded-2xl bg-gray-900/40 border border-gray-700">
            <i class="fas fa-inbox text-3xl text-gray-600 mb-3 block"></i>
            <p class="text-gray-400 text-sm">مرجوعی‌ای ثبت نشده</p>
          </div>
        </div>

        <!-- Desktop table -->
        <div class="hidden md:block w-full max-w-full min-w-0 overflow-x-auto table-scroll-safe">
          <table class="w-full min-w-[900px] text-sm">
            <thead class="bg-gray-900/70 border-b border-gray-700">
            <tr>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">تاریخ</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">محصول</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">سفارش</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">تعداد</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">قیمت هر عدد</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">مبلغ</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">بهای کسرشده</th>
            </tr>
            </thead>

            <tbody class="divide-y divide-gray-700/50">
            <tr v-for="(tx, idx) in transactions" :key="idx" class="hover:bg-gray-700/30 transition">
              <td class="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">{{ tx.date | toJalali('jYYYY/jMM/jDD HH:mm') }}</td>
              <td class="px-4 py-3">
                <div class="flex items-center gap-2 min-w-[220px] max-w-[320px] overflow-hidden">
                  <div
                      class="shrink-0 w-9 h-9 aspect-square rounded-lg overflow-hidden border border-gray-700 bg-gray-800 flex items-center justify-center"
                      :class="imgOk(tx.image) ? 'cursor-zoom-in' : ''"
                      @click="zoomImage(tx.image)"
                  >
                    <img v-if="imgOk(tx.image)" :src="img_source(tx.image)" class="w-full h-full object-cover" @error="markBroken(tx.image)">
                    <i v-else class="fas fa-image text-gray-600 text-xs"></i>
                  </div>
                  <div class="min-w-0">
                    <p class="text-xs text-gray-100 font-medium truncate">{{ tx.product_name }}</p>
                    <p v-if="tx.barcode" class="text-[10px] text-gray-500 truncate" dir="ltr">{{ tx.barcode }}</p>
                  </div>
                </div>
              </td>
              <td class="px-4 py-3 text-xs text-gray-400 whitespace-nowrap text-right" dir="ltr">{{ tx.order_reference }}</td>
              <td class="px-4 py-3 text-xs text-gray-300 whitespace-nowrap text-right" dir="ltr">{{ formatNumber(tx.quantity) }}</td>
              <td class="px-4 py-3 text-xs text-gray-300 whitespace-nowrap text-right" dir="ltr">{{ formatMoney(tx.unit_price) }}</td>
              <td class="px-4 py-3 text-xs text-rose-300 font-bold whitespace-nowrap text-right" dir="ltr">{{ formatMoney(tx.line_total) }}</td>
              <td class="px-4 py-3 text-xs text-gray-400 whitespace-nowrap text-right" dir="ltr">{{ formatMoney(tx.line_cost) }}</td>
            </tr>

            <tr v-if="transactions.length === 0">
              <td colspan="7" class="px-4 py-12 text-center">
                <i class="fas fa-inbox text-3xl text-gray-600 mb-3 block"></i>
                <p class="text-gray-400 text-sm">مرجوعی‌ای ثبت نشده</p>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-else class="w-full rounded-2xl border border-red-500/30 bg-red-950/40 px-4 py-5 text-right">
      <h3 class="text-sm font-bold text-red-100">دسترسی غیرمجاز</h3>
      <p class="mt-2 text-xs text-red-200">دسترسی برای شما مجاز نیست.</p>
    </div>

    <!-- Image zoom overlay -->
    <div
        v-if="zoomedImage"
        @click="zoomedImage = null"
        class="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-6 cursor-zoom-out"
    >
      <img :src="zoomedImage" class="max-w-full max-h-full rounded-2xl shadow-2xl object-contain" @click.stop>
      <button
          @click="zoomedImage = null"
          class="absolute top-4 left-4 sm:top-6 sm:left-6 w-9 h-9 rounded-full bg-gray-900/80 border border-gray-600 text-gray-200 hover:bg-gray-800 transition flex items-center justify-center text-lg"
      >
        <i class="fas fa-times"></i>
      </button>
    </div>
  </div>
</template>

<script>
import getPartnerCalculationsApi from './getPartnerCalculationsApi'
import date from "@/components/mcomponents/cleave/Date"

export default {
  name: 'PartnerCalculationsReturns',

  mixins: [getPartnerCalculationsApi],

  components: { date },

  props: {
    partnerId: {
      type: [String, Number],
      default: null,
    },
  },

  data() {
    return {
      loading: false,
      error: null,

      fromDate: '',
      toDate: '',
      gtin: '',

      summary: null,
      topReturnedProducts: [],
      transactions: [],

      zoomedImage: null,
      brokenImages: {},
    }
  },

  computed: {
    isAdminMode() {
      return !!this.partnerId
    },
    isAuthorized() {
      if (this.isAdminMode) return this.user.user_type === 'ma' || this.user.is_superuser
      return this.user.user_type === 'prtnr' || this.user.user_type === 'ma'
    },
  },

  watch: {
    partnerId() {
      this.fetchData()
    },
  },

  methods: {
    getParams() {
      const params = {}
      if (this.fromDate) params.from = this.fromDate
      if (this.toDate) params.to = this.toDate
      if (this.gtin) params.gtin = this.gtin
      return params
    },

    fetchData() {
      this.loading = true
      this.error = null

      const onSuccess = data => {
        this.summary = data.summary
        this.topReturnedProducts = data.top_returned_products || []
        this.transactions = data.transactions || []
        this.loading = false
      }
      const onError = () => {
        this.error = 'خطا در دریافت اطلاعات'
        this.loading = false
      }

      if (this.isAdminMode) {
        this.getAdminPartnerCalculationsReturns(this.partnerId, this.getParams(), onSuccess, onError)
      } else {
        this.getPartnerCalculationsReturns(this.getParams(), onSuccess, onError)
      }
    },

    applyFilter() {
      this.fetchData()
    },

    clearFilter() {
      this.fromDate = ''
      this.toDate = ''
      this.gtin = ''
      this.fetchData()
    },

    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '۰'
      const number = Number(value)
      if (Number.isNaN(number)) return '۰'
      return new Intl.NumberFormat('fa-IR').format(number)
    },

    formatMoney(value) {
      return `${this.formatNumber(Math.round(Number(value || 0)))} تومان`
    },

    // اگه عکس واقعاً لود نشه (فایل حذف‌شده/لینک خراب)، v-if صرفاً وجود URL رو چک می‌کنه
    // نه لود شدنش -- برای همین با @error این URL رو به‌عنوان خراب علامت می‌زنیم تا آیکون
    // جایگزین نشون داده بشه، نه یک باکس خالی.
    imgOk(url) {
      return !!url && !this.brokenImages[url]
    },

    markBroken(url) {
      this.$set(this.brokenImages, url, true)
    },

    zoomImage(url) {
      if (this.imgOk(url)) this.zoomedImage = this.img_source(url)
    },
  },

  mounted() {
    this.fetchData()
  },
}
</script>

<style scoped>
table {
  border-collapse: collapse;
}

.table-scroll-safe {
  scrollbar-width: thin;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.break-safe {
  overflow-wrap: anywhere;
  word-break: break-word;
}

.min-w-0 {
  min-width: 0 !important;
}
</style>
